import router from '@/router'
import { useUserStore } from '@/modules/users/stores/user.store'
import { useClientStore } from '@/modules/clients/stores/client.store'
import { HttpMethod, HttpStatusCode } from './types/http.enums'
import { ROUTE_NAMES } from '@/router/constants/route.constants'
import type { HttpRequest, HttpResponseType, ResourcePath } from './types/http.request'
import type { ErrorResponse, HttpResponse } from './types/http.response'

// TODO: Consider letting tryFetch accept path segments:
// tryFetch(method, baseUrl, ["users", userId ?? "me", "settings"], options)
// so endpoint construction is centralized.

const API_VERSION_PATH = import.meta.env.VITE_API_VERSION_PATH

const HEADER_CONTENT_TYPE = 'Content-Type'
const HEADER_CONTENT_TYPE_VALUE = 'application/json'
const HEADER_ADDIMOTION_CLIENT = 'Addimotion-Client'

const MODE_CORS = 'cors'
const CREDENTIALS = 'include'

const buildHeaders = (request: RequestInit, body?: unknown): HeadersInit => {
  const headers = new Headers(request.headers)

  const userStore = useUserStore()
  if (userStore.user && userStore.user.activeClient) {
    headers.set(HEADER_ADDIMOTION_CLIENT, userStore.user.activeClient.clientKey)
  }

  if (!(body instanceof FormData)) {
    headers.set(HEADER_CONTENT_TYPE, HEADER_CONTENT_TYPE_VALUE)
  }

  return headers
}

const buildUrl = (
  baseUrl: string,
  resource: ResourcePath,
  useView = false,
  queryParams?: Record<string, string | number | boolean | null | undefined>,
): URL => {
  const clientStore = useClientStore()

  const resourcePath = resource.map((segment) => encodeURIComponent(String(segment))).join('/')
  const endpoint = useView ? `views/${clientStore.activeViewId}/${resourcePath}` : resourcePath
  const url = new URL(`${API_VERSION_PATH}/${endpoint}`, baseUrl)

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, String(value))
      }
    })
  }

  return url
}

const handleHttpRedirects = async (status: number) => {
  switch (status) {
    case HttpStatusCode.Unauthorized:
      await router.push({ name: ROUTE_NAMES.UNAUTHORIZED })
      break
    case HttpStatusCode.Forbidden:
      await router.push({ name: ROUTE_NAMES.FORBIDDEN })
      break
    case HttpStatusCode.ServiceUnavailable:
      await router.push({ name: ROUTE_NAMES.SERVICE_UNAVAILABLE })
      break
  }
}

const parseResponse = async <T>(response: Response, responseType: HttpResponseType): Promise<T> => {
  switch (responseType) {
    case 'blob':
      return (await response.blob()) as T
    case 'text':
      return (await response.text()) as T
    case 'arrayBuffer':
      return (await response.arrayBuffer()) as T
    case 'json':
    default: {
      const contentType = response.headers.get(HEADER_CONTENT_TYPE) || ''
      if (contentType.includes(HEADER_CONTENT_TYPE_VALUE)) {
        return (await response.json()) as T
      }
      return (await response.text()) as T
    }
  }
}

export const tryFetch = async <TResponse, TErrorCode, TBody = unknown>(
  method: HttpMethod,
  baseUrl: string,
  resource: ResourcePath,
  options?: HttpRequest<TBody>,
): Promise<HttpResponse<TResponse, TErrorCode>> => {
  const request: RequestInit = {
    method,
    mode: MODE_CORS,
    credentials: CREDENTIALS,
  }

  try {
    request.headers = buildHeaders(request, options?.body)

    if (options?.body !== undefined) {
      request.body = options.body instanceof FormData ? options.body : JSON.stringify(options.body)
    }

    const response = await fetch(
      buildUrl(baseUrl, resource, options?.view, options?.queryParams),
      request,
    )

    const status = response.status
    if (status === HttpStatusCode.NoContent) return { status }

    const responseType: HttpResponseType = options?.responseType ?? 'json'
    const data = await parseResponse<unknown>(response, responseType)

    if (!response.ok) {
      await handleHttpRedirects(status)
      return {
        status,
        error: data as ErrorResponse<TErrorCode>,
      }
    }

    return {
      status,
      data: data as TResponse,
    }
  } catch (error: unknown) {
    return {
      status: 0,
      error: {
        errorCode: undefined as TErrorCode,
        context: {
          message: error instanceof Error ? error.message : 'Network error',
        },
      },
    }
  }
}
